import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Clock, XCircle, Share2, Download, Home, Loader2 } from "lucide-react";
import api from "../lib/api";
import { formatRupiah, formatDateTime, txnDisplay, STATUS_LABEL, TXN_TYPE_LABEL, maskAccount } from "../lib/format";
import { BrandLogo } from "../components/Layout";

const statusConfig = {
  SUCCESS: { Icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", label: "Transaksi Berhasil" },
  PENDING: { Icon: Clock, color: "text-amber-500", bg: "bg-amber-50", label: "Sedang Diproses" },
  FAILED: { Icon: XCircle, color: "text-red-600", bg: "bg-red-50", label: "Transaksi Gagal" },
};

const DetailRow = ({ label, value, mono = false }) => (
  <div className="flex justify-between gap-3 text-sm py-1.5">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className={`font-medium text-right ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);

export default function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [txn, setTxn] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get(`/transactions/${id}`);
      setTxn(res.data);
      return res.data;
    } catch {
      setNotFound(true);
      return null;
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (txn?.status === "PENDING" && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const t = await load();
        if (t && t.status !== "PENDING") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (t.status === "SUCCESS") toast.success("Transfer berhasil diselesaikan!");
        }
      }, 3000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txn?.status]);

  const share = () => {
    if (!txn) return;
    const { title } = txnDisplay(txn);
    const text = `Bukti Transaksi Quantum Pay\n${title}\nNominal: ${formatRupiah(txn.amount)}\nStatus: ${STATUS_LABEL[txn.status]}\nRef: ${txn.reference}\nTanggal: ${formatDateTime(txn.created_at)}`;
    navigator.clipboard.writeText(text);
    toast.success("Bukti transaksi disalin ke clipboard");
  };

  const downloadPdf = async () => {
    if (!txn) return;
    setDownloading(true);
    try {
      const res = await api.get(`/transactions/${txn.id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bukti-${txn.reference || txn.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Bukti PDF berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (notFound) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <p className="font-semibold">Transaksi tidak ditemukan</p>
        <Button className="mt-4" onClick={() => navigate("/")}>Kembali ke Beranda</Button>
      </div>
    );
  }

  if (!txn) {
    return (
      <div className="max-w-md mx-auto space-y-4">
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-60 w-full rounded-2xl" />
      </div>
    );
  }

  const cfg = statusConfig[txn.status] || statusConfig.PENDING;
  const { title } = txnDisplay(txn);
  const isIn = txn.direction === "in";

  return (
    <div className="max-w-md mx-auto space-y-4">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <Card className="rounded-2xl printable-receipt shadow-[0_10px_30px_rgba(11,18,32,0.08)]">
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <BrandLogo />
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground border rounded-full px-2 py-0.5">Bukti Transaksi</span>
            </div>

            {/* Status */}
            <div className="text-center mb-6">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                className={`mx-auto h-16 w-16 rounded-full ${cfg.bg} flex items-center justify-center mb-3`}
              >
                <cfg.Icon className={`h-9 w-9 ${cfg.color} ${txn.status === "PENDING" ? "animate-pulse" : ""}`} />
              </motion.div>
              <p className={`font-heading font-semibold ${cfg.color}`} data-testid="receipt-status">{cfg.label}</p>
              {txn.status === "PENDING" && <p className="text-xs text-muted-foreground mt-1">Dana sedang dikirim ke bank tujuan (±beberapa detik)...</p>}
              <p className="font-heading text-3xl font-bold tabular-nums mt-3" data-testid="receipt-amount">
                {isIn ? "+" : "-"}{formatRupiah(txn.amount)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{title}</p>
            </div>

            <Separator className="my-4" />

            {/* Details */}
            <div className="space-y-0.5" data-testid="receipt-details">
              <DetailRow label="No. Referensi" value={txn.reference} mono />
              <DetailRow label="Jenis" value={TXN_TYPE_LABEL[txn.type] || txn.type} />
              <DetailRow label="Tanggal" value={formatDateTime(txn.created_at)} />
              {txn.type === "transfer_bank" && (
                <>
                  <DetailRow label="Bank Tujuan" value={txn.bank_name} />
                  <DetailRow label="No. Rekening" value={maskAccount(txn.account_number)} mono />
                  <DetailRow label="Penerima" value={txn.account_holder} />
                  {txn.gateway && <DetailRow label="Jalur" value={txn.gateway === "xendit" ? "Xendit Sandbox" : "Simulasi Internal"} />}
                </>
              )}
              {txn.type === "transfer_wallet" && (
                <DetailRow label={isIn ? "Pengirim" : "Penerima"} value={`${txn.counterparty_name || "-"}${txn.counterparty_email ? ` (${txn.counterparty_email})` : ""}`} />
              )}
              {txn.type === "topup_va" && (
                <>
                  <DetailRow label="Bank" value={txn.bank_name} />
                  {txn.va_number && <DetailRow label="No. VA" value={txn.va_number} mono />}
                  {txn.gateway && <DetailRow label="Jalur" value={txn.gateway === "xendit" ? "Xendit Sandbox" : "Simulasi Internal"} />}
                </>
              )}
              {(txn.type === "qris_payment" || txn.type === "qris_receive") && (
                <>
                  <DetailRow label={isIn ? "Dibayar oleh" : "Merchant"} value={txn.counterparty_name || "-"} />
                  {txn.qris_id && <DetailRow label="ID QRIS" value={txn.qris_id} mono />}
                </>
              )}
              {txn.note && <DetailRow label="Catatan" value={txn.note} />}
              <DetailRow label="Biaya Admin" value={txn.fee > 0 ? formatRupiah(txn.fee) : "Gratis"} />
              <Separator className="my-2" />
              <div className="flex justify-between text-sm py-1.5">
                <span className="font-semibold">Total</span>
                <span className="font-heading font-bold tabular-nums">{formatRupiah(txn.amount + (txn.fee || 0))}</span>
              </div>
            </div>

            <p className="text-[10px] text-muted-foreground text-center mt-4">
              Transaksi diproses oleh Quantum Pay — Mode Sandbox (bukan alat pembayaran yang sah).
            </p>
          </CardContent>
        </Card>
      </motion.div>

      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" onClick={share} data-testid="receipt-share-button">
          <Share2 className="h-4 w-4 mr-1.5" /> Bagikan
        </Button>
        <Button variant="outline" onClick={downloadPdf} disabled={downloading} data-testid="receipt-download-button">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Download className="h-4 w-4 mr-1.5" /> Unduh PDF</>)}
        </Button>
      </div>
      <Button className="w-full h-11" onClick={() => navigate("/")} data-testid="receipt-back-home-button">
        <Home className="h-4 w-4 mr-1.5" /> Kembali ke Beranda
      </Button>
    </div>
  );
}
